import React, { useEffect, useState } from 'react';
import { Box, Typography, Chip, Button } from '@mui/material';
import { CreditCard } from 'mdi-material-ui';
import { formatCurrency } from '../../utils/formatting';
import { computeEventPayout } from '../../services/eventService';
import { getPaymentTypes } from '../../services/paymentsService';

const EPSILON = 0.5;

const StatRow = ({ label, value, bold, color }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
    <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: bold ? 700 : 400, color: color || 'inherit' }}>
      {formatCurrency(value)}
    </Typography>
  </Box>
);

/**
 * Odeme/hak edis durumu.
 * inline=true: Sale Report Summary kartinin icinde, kenarliksiz satirlar.
 * Buton sadece admin'e gosterilir; rakamlar herkese acik.
 */
const PaymentStatus = ({
  event,
  eventStatsData,
  isAdmin = false,
  onOpenPayment,
  inline = false,
  embedded = false,
}) => {
  const [paymentTypes, setPaymentTypes] = useState([]);
  useEffect(() => {
    getPaymentTypes().then(setPaymentTypes);
  }, []);

  const { netOwed, paid, remainingToPay } = computeEventPayout(event, eventStatsData);

  let status = { label: 'Ödenmedi', color: 'error' };
  if (paid > EPSILON && remainingToPay > EPSILON) {
    status = { label: 'Kısmi Ödendi', color: 'warning' };
  } else if (paid > EPSILON && remainingToPay <= EPSILON) {
    status = { label: 'Ödendi', color: 'success' };
  }

  const rows = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: inline ? 1 : 1.5 }}>
        {!inline && (
          <Typography variant="h6" sx={{ m: 0, fontSize: embedded ? undefined : 20 }}>
            Ödeme Durumu
          </Typography>
        )}
        {inline && (
          <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
            Ödeme Durumu
          </Typography>
        )}
        <Chip label={status.label} color={status.color} size="small" />
      </Box>

      <StatRow label="Net Hak Ediş (Ödenecek Toplam)" value={netOwed} bold color="#1976d2" />
      {event?.payoutExtraByType &&
        typeof event.payoutExtraByType === 'object' &&
        Object.entries(event.payoutExtraByType)
          .filter(([, v]) => Number(v) > EPSILON)
          .sort(([a], [b]) => String(a).localeCompare(String(b)))
          .map(([type, v]) => {
            const label = paymentTypes.find((pt) => pt.value === type)?.label ?? type;
            return <StatRow key={type} label={label} value={Number(v)} />;
          })}
      <StatRow label="Ödenen" value={paid} />
      <StatRow
        label="Kalan"
        value={remainingToPay}
        bold
        color={remainingToPay > EPSILON ? '#d32f2f' : '#2e7d32'}
      />

      {event?.payNote ? (
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #DDD' }}>
          <Typography variant="caption" color="text.secondary">
            Not
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {event.payNote}
          </Typography>
        </Box>
      ) : null}

      {isAdmin && onOpenPayment ? (
        <Button
          onClick={onOpenPayment}
          variant="contained"
          size="small"
          startIcon={<CreditCard />}
          sx={{ mt: 1.5 }}
        >
          {paid > EPSILON ? 'Ödemeyi Güncelle' : 'Ödeme Yap'}
        </Button>
      ) : null}
    </>
  );

  if (inline) return <Box sx={{ mt: 1 }}>{rows}</Box>;

  const card = (
    <Box
      sx={{
        width: '100%',
        maxWidth: embedded ? '100%' : 420,
        bgcolor: '#ffffff',
        border: '1px solid #e6e6e6',
        borderRadius: embedded ? 2 : 1,
        p: embedded ? 2 : 1.5,
      }}
    >
      {rows}
    </Box>
  );

  if (embedded) return card;

  return <Box sx={{ paddingTop: 1.5, paddingBottom: 1.5 }}>{card}</Box>;
};

export default PaymentStatus;
